import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BellOff, Plus, Undo2 } from "lucide-react";

const SOURCE_LABEL = { link: "Unsubscribe link", reply_stop: "Replied STOP", admin: "Added by admin", preferences: "Preference centre" };

export default function BroadcastOptOuts() {
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  // items stays null while loading so the heading never shows a false "(0)".
  const load = () => { setLoadError(""); return api.get("/admin/broadcast/unsubscribed").then(({ data }) => setItems(data.items || [])).catch((e) => setLoadError(e.response?.status === 401 ? "Session expired — sign in again." : (e.response?.data?.detail || "Couldn't load the opt-out list."))); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post("/admin/broadcast/unsubscribed", { email, note: "Replied STOP (logged manually)" });
      toast.success(data.already ? "Already opted out" : `${email} opted out of announcements`);
      setEmail(""); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    } finally { setAdding(false); }
  };
  const resubscribe = async (u) => {
    if (!window.confirm(`Re-subscribe ${u.email} to announcements?`)) return;
    try { await api.delete(`/admin/broadcast/unsubscribed/${u.id}`); toast.success("Re-subscribed"); load(); } catch { toast.error("Could not re-subscribe"); }
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="broadcast-opt-outs">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="tracking-overline text-[10px] text-[#264839] inline-flex items-center gap-1.5"><BellOff className="w-3 h-3" /> Opted out</div>
          <h2 className="font-serif text-2xl text-[#11211A] mt-1">Unsubscribed from marketing & reminders{items ? ` (${items.length})` : ""}</h2>
          <p className="text-xs text-[#7D8C85] mt-1">Excluded from every broadcast audience and from all automated marketing/reminder emails (claim reminders, upgrade nudges, image and onboarding reminders, enquiry and quote reminders, waitlist digests). Added via the one-click link in each email, a “STOP” reply, or manually below. Account, password and quote-request emails are unaffected.</p>
        </div>
        <form onSubmit={add} className="flex items-center gap-2" data-testid="opt-out-add-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Log a STOP reply: supplier@email.com" className="px-3 py-2 border border-[#C4CFC9] text-sm bg-white w-72" data-testid="opt-out-email" />
          <button type="submit" disabled={adding || !email.trim()} className="btn-secondary text-xs disabled:opacity-50 inline-flex items-center gap-1" data-testid="opt-out-add"><Plus className="w-3.5 h-3.5" /> Opt out</button>
        </form>
      </div>
      {loadError ? <div className="text-sm text-[#B35C4A] flex items-center gap-3" data-testid="opt-out-error">{loadError} <button type="button" onClick={load} className="text-xs underline text-[#264839]" data-testid="opt-out-retry">Retry</button></div>
        : !items ? <div className="text-sm text-[#7D8C85] animate-pulse" data-testid="opt-out-loading">Loading opt-outs…</div> : items.length === 0 ? <div className="text-sm text-[#7D8C85]" data-testid="opt-out-empty">Nobody has opted out yet.</div> : (
        <table className="w-full text-sm" data-testid="opt-out-table">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#7D8C85] border-b border-[#E8ECE9]">
            <tr><th className="py-2">Email</th><th>Company</th><th>How</th><th>When</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-[#F0EFEA]" data-testid={`opt-out-row-${u.id}`}>
                <td className="py-2 text-[#11211A]">{u.email}</td>
                <td className="py-2 text-[#7D8C85]">{u.non_account ? <span className="text-[11px] uppercase tracking-wider text-[#B35C4A]" data-testid={`opt-out-nonaccount-${u.id}`}>No account (buyer)</span> : (u.company || u.name || "—")}</td>
                <td className="py-2 text-[#7D8C85]" title={u.broadcast_unsubscribed_note || ""}>{SOURCE_LABEL[u.broadcast_unsubscribed_source] || u.broadcast_unsubscribed_source || "—"}{u.broadcast_unsubscribed_note ? <span className="block text-[11px] text-[#7D8C85]/80 truncate max-w-[260px]">{u.broadcast_unsubscribed_note}</span> : null}</td>
                <td className="py-2 text-[#7D8C85] tabular-nums">{(u.broadcast_unsubscribed_at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="py-2 text-right"><button type="button" onClick={() => resubscribe(u)} className="text-[11px] text-[#264839] hover:underline inline-flex items-center gap-1" data-testid={`opt-out-resubscribe-${u.id}`}><Undo2 className="w-3 h-3" /> Re-subscribe</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
